import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import { Stack, Typography } from "@mui/material";
import { formatCurrency } from "@zfp-akademie-a-s/contract-details";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as UserIcon } from "Components/_Shared/Icons/Assets/user-male.svg";
import {
  ContractProposalBaseInvestmentToolType,
  type ContractProposalBaseSolutionHorizonType,
  type ContractProposalPortfolio,
  CurrentContractListItemProposedStepActionType,
  type NsurePlanOverview,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import { inflectYears } from "Infrastructure/Utils/LanguageUtils";
import { type FC, type ReactNode, startTransition, useContext, useEffect, useState } from "react";
import { Dot } from "../../_Shared/Dot";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { Tag, type TagColor } from "../_Shared/Tag";
import { CurrentSolutionDrawer } from "./CurrentSolutionDrawer";
import { GoalFulfillmentSolutionItemWarning } from "./GoalFulfillmentSolutionItemWarning";
import { GoalFulfillmentFilterType, useGoalsFulfillmentContext } from "./GoalsFulfillmentContext";
import { ProposedSolutionDrawer } from "./ProposedSolutionDrawer";
import { SolutionType } from "./types";
import { useProposalNsureEvaluation } from "./useProposalNsureEvaluation";

type Props = {
  solutionType: SolutionType;
  solutionID: string;
  title: string;
  product?: string;
  proposedSolutionAction: CurrentContractListItemProposedStepActionType;
  clientName: string;
  appreciationRate: number;
  solutionHorizon: number;
  regularAmount: number;
  oneTimeAmount: number;
  futureAmount: number;
  regularAmountChange?: number;
  oneTimeAmountChange?: number;
  horizonType: ContractProposalBaseSolutionHorizonType;
  investmentToolType: ContractProposalBaseInvestmentToolType;
  isNsure?: boolean;
  nsurePlan?: NsurePlanOverview;
  portfolio?: ContractProposalPortfolio | null;
};

const stepActionMap: Record<CurrentContractListItemProposedStepActionType, { color: TagColor; icon?: ReactNode }> = {
  [CurrentContractListItemProposedStepActionType.Keep]: { color: "success" },
  [CurrentContractListItemProposedStepActionType.Cancel]: { color: "error" },
  [CurrentContractListItemProposedStepActionType.Modify]: { color: "warning" },
  [CurrentContractListItemProposedStepActionType.Contracting]: { color: "success" },
};

export const GoalFulfillmentSolutionItem: FC<Props> = ({
  solutionType,
  solutionID,
  title,
  product,
  proposedSolutionAction,
  clientName,
  appreciationRate,
  solutionHorizon,
  regularAmount,
  oneTimeAmount,
  futureAmount,
  regularAmountChange = 0,
  oneTimeAmountChange = 0,
  horizonType,
  investmentToolType,
  isNsure,
  nsurePlan,
  portfolio,
}) => {
  const { t } = useResource();

  const [editCurrentSolutionDrawerOpen, setEditCurrentSolutionDrawerOpen] = useState(false);
  const [proposedSolutionDrawerOpen, setProposedSolutionDrawerOpen] = useState(false);

  const isProposedActionModify = proposedSolutionAction === CurrentContractListItemProposedStepActionType.Modify;
  const isProposedActionCancel = proposedSolutionAction === CurrentContractListItemProposedStepActionType.Cancel;
  const proposedSolutionActionData = stepActionMap[proposedSolutionAction];
  const isValidProposedAction = Object.values(CurrentContractListItemProposedStepActionType).includes(proposedSolutionAction);

  const { isFilterSelected, registerEvaluationWarning, registerEvaluationPending, isInvestmentCaseLocked } = useGoalsFulfillmentContext();
  const { readOnly } = useContext(AnalysisDetailContext);
  const shouldShowFutureAmount = isFilterSelected(GoalFulfillmentFilterType.FutureAmount);

  const {
    hasWarning: hasEvaluationWarning,
    noQuestionnaireEvaluation,
    hasError: hasEvaluationError,
    isPending: isEvaluationPending,
  } = useProposalNsureEvaluation({
    portfolio,
    oneTimeAmount,
    regularAmount,
    nsurePlan,
  });

  useEffect(() => {
    registerEvaluationWarning(solutionID, !isEvaluationPending && (hasEvaluationWarning || hasEvaluationError));
    return () => registerEvaluationWarning(solutionID, false);
  }, [solutionID, hasEvaluationWarning, hasEvaluationError, isEvaluationPending, registerEvaluationWarning]);

  useEffect(() => {
    registerEvaluationPending(solutionID, isEvaluationPending);
    return () => registerEvaluationPending(solutionID, false);
  }, [solutionID, isEvaluationPending, registerEvaluationPending]);

  const isPortfolioClickBlocked = isInvestmentCaseLocked && portfolio != null;
  const isDrawerOpenBlocked = readOnly || isPortfolioClickBlocked;

  const handleOpenDrawer = () => {
    if (isDrawerOpenBlocked) return;
    if (solutionType === SolutionType.Current) {
      startTransition(() => setEditCurrentSolutionDrawerOpen(true));
    } else {
      startTransition(() => setProposedSolutionDrawerOpen(true));
    }
  };

  const getActionTitle = () => {
    if (isProposedActionModify) {
      return t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions.Modify);
    }
    return investmentToolType === ContractProposalBaseInvestmentToolType.commodity
      ? t(Resources.AnalysisDetail.GoalsFulfillment.CommoditiesSolutionOptions[proposedSolutionAction])
      : t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions[proposedSolutionAction]);
  };

  const getCurrentDepositAmount = (currentValue: number, depositChange: number) => {
    if (isProposedActionCancel) {
      return (
        <Typography variant="body2" color="text.secondary" fontWeight={300} sx={{ textDecoration: "line-through", whiteSpace: "nowrap" }}>
          {formatCurrencyWithoutCode(currentValue)}
        </Typography>
      );
    }
    if (isProposedActionModify) {
      const newValue = depositChange + currentValue;
      const isIncrease = newValue > currentValue;
      return (
        <>
          {depositChange !== 0 && (
            <HStack gap={Spacing.M}>
              {isIncrease ? (
                <NorthEastIcon sx={{ fontSize: "15px", color: "warning.dark" }} />
              ) : (
                <SouthEastIcon sx={{ fontSize: "15px", color: "warning.dark" }} />
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={300}
                sx={{ textDecoration: "line-through", whiteSpace: "nowrap" }}
              >
                {formatCurrencyWithoutCode(currentValue)}
              </Typography>
            </HStack>
          )}
          <Typography variant="body2" color="text.primary" fontWeight={500} sx={{ whiteSpace: "nowrap" }}>
            {formatCurrencyWithoutCode(newValue)}
          </Typography>
        </>
      );
    }

    return (
      <Typography variant="body2" color="text.primary" fontWeight={500} sx={{ whiteSpace: "nowrap" }}>
        {formatCurrencyWithoutCode(currentValue)}
      </Typography>
    );
  };

  return (
    <>
      <HStack
        justifyContent="flex-start"
        alignItems="center"
        gap={Spacing.L}
        sx={{
          padding: Spacing.L,
          background: "white",
          cursor: isDrawerOpenBlocked ? "default" : "pointer",
        }}
        onClick={handleOpenDrawer}
      >
        <Stack spacing={Spacing.S} width="100%" direction="column" justifyContent="flex-start">
          <GoalFulfillmentSolutionItemWarning
            isEvaluationPending={isEvaluationPending}
            hasEvaluationWarning={hasEvaluationWarning}
            hasEvaluationError={hasEvaluationError}
            noQuestionnaireEvaluation={noQuestionnaireEvaluation}
          />
          <HStack alignItems="center" gap={Spacing.XS} justifyContent={"space-between"}>
            <Typography variant="body2" color="textPrimary" fontWeight={600}>
              {title}
            </Typography>
          </HStack>
          {product && (
            <Typography variant="body2" color="textPrimary" fontWeight={400}>
              {product}
            </Typography>
          )}
          <HStack justifyContent="flex-start" alignItems="center" gap={Spacing.M} divider={<Dot />}>
            <HStack justifyContent="flex-end" alignItems="flex-end" gap={Spacing.S} sx={{ whiteSpace: "nowrap" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={400}>
                {t(Resources.AnalysisDetail.Goals.InterestRate)}
              </Typography>
              <Typography variant="caption" color="text.primary" fontWeight={500}>
                {appreciationRate}% p.a.
              </Typography>
            </HStack>

            <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" gap={Spacing.S} sx={{ whiteSpace: "nowrap" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={400}>
                {t(Resources.AnalysisDetail.GoalsFulfillment.Horizon)}
              </Typography>
              <Typography variant="caption" color="text.primary" fontWeight={500}>
                {inflectYears(solutionHorizon, t)}
              </Typography>
            </Stack>
          </HStack>

          {proposedSolutionAction !== CurrentContractListItemProposedStepActionType.Cancel && shouldShowFutureAmount && (
            <Stack direction="row" justifyContent="flex-start" alignItems="center" gap={Spacing.M} sx={{ whiteSpace: "nowrap" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={400}>
                {t(Resources.AnalysisDetail.GoalsFulfillment.FutureAmount)}
              </Typography>
              <Typography variant="caption" color="text.primary" fontWeight={500}>
                {formatCurrency(futureAmount)}
              </Typography>
            </Stack>
          )}
          <Flex>
            {clientName && investmentToolType === ContractProposalBaseInvestmentToolType.pension_saving && (
              <Tag variant="solid" color="info" title={clientName} icon={<UserIcon width={12} height={12} />} />
            )}
          </Flex>
        </Stack>

        <Flex justifyContent={"center"} alignItems="center" alignSelf={"stretch"} width={"150px"}>
          {isValidProposedAction && (
            <Tag
              variant="solid"
              color={proposedSolutionActionData.color}
              title={getActionTitle()}
              {...(proposedSolutionActionData.icon && { icon: proposedSolutionActionData.icon })}
            />
          )}
        </Flex>

        <Stack direction="column" minWidth={230} spacing={Spacing.S}>
          <HStack justifyContent="flex-end" alignItems="flex-end" gap={Spacing.S}>
            {solutionType === SolutionType.Current ? (
              getCurrentDepositAmount(regularAmount, regularAmountChange)
            ) : (
              <Typography variant="body2" color="text.primary" fontWeight={500} sx={{ whiteSpace: "nowrap" }}>
                {formatCurrencyWithoutCode(regularAmount)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ whiteSpace: "nowrap" }}>
              {t(Resources.Common.CZKSuffix)}
              {`/${t(Resources.Common.Month)}`}
            </Typography>
          </HStack>

          <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" gap={Spacing.S}>
            {solutionType === SolutionType.Current ? (
              getCurrentDepositAmount(oneTimeAmount, oneTimeAmountChange)
            ) : (
              <Typography variant="body2" color="text.primary" fontWeight={500} sx={{ whiteSpace: "nowrap" }}>
                {formatCurrencyWithoutCode(oneTimeAmount)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ whiteSpace: "nowrap" }}>
              {t(Resources.Common.CZKSuffix)}
            </Typography>
          </Stack>
        </Stack>
      </HStack>

      <CurrentSolutionDrawer
        anchor="right"
        open={editCurrentSolutionDrawerOpen}
        onClose={() => setEditCurrentSolutionDrawerOpen(false)}
        solutionId={solutionID}
        investmentToolType={investmentToolType}
        horizonType={horizonType}
      />

      <ProposedSolutionDrawer
        anchor="right"
        open={proposedSolutionDrawerOpen}
        onClose={() => setProposedSolutionDrawerOpen(false)}
        solutionId={solutionID}
        investmentToolType={investmentToolType}
        horizonType={horizonType}
        isNsure={isNsure}
        nsurePlan={nsurePlan}
      />
    </>
  );
};
